import type { ApiReference } from "./openapi-data"

/*
   A brief an AI agent (Claude Code, a script-writing assistant) can be handed to work against
   this deployment. Built from the live schema, so the endpoint list is the deployed one. It names
   the key's environment variable and never a key: whoever pastes this does not paste a secret.
*/

export function agentInstructions(origin: string, reference: ApiReference | null): string {
  const evalRuns = reference?.tags.find((t) => t.name === "eval-runs")
  const endpoints = evalRuns
    ? evalRuns.operations
        .map((op) => `- \`${op.method} ${op.path}\`${op.summary ? ` — ${op.summary}` : ""}`)
        .join("\n")
    : "- (read them from the schema, tag `eval-runs`)"

  return `# Vela API — instructions for agents

You are calling the Vela API (EPA pesticide label review by Pyxis Regulatory Consulting).

## Connection

- Base URL: ${origin}
- Authenticate every request with the header \`X-API-Key: $PYXIS_API_KEY\`. The key is in the
  environment variable \`PYXIS_API_KEY\`. Never print it, log it, write it to a file, or put it
  on a command line other than as that variable.
- The full contract (OpenAPI 3) is \`GET ${origin}/api/v1/openapi.json\` with the same header.
  Read it before calling an endpoint you have not used; do not guess request shapes.
- Lists return a bare JSON array, paged with \`limit\` / \`offset\`, with the total in the
  \`X-Total-Count\` response header. Page until you have \`X-Total-Count\` rows.
- Errors are JSON with a \`detail\` field. \`401\`: the key is missing or revoked. \`403\`: this
  key may not do that. \`404\`: the item does not exist or is outside this key's scope.

## Publishing evaluation runs

An evaluation run shows our admins how the review answered labels a client's experts marked up.
Runs are an admin-only development tool and are never shown to clients.

1. \`POST /api/v1/eval-runs\` with \`EvalRunCreate\` (\`name\`, \`dataset\`, \`method\`, \`summary\`
   figures, optional \`client_id\`). The run is created as a **draft**.
2. For each label, \`POST /api/v1/eval-runs/{run_id}/labels\` as \`multipart/form-data\`: \`mid\`,
   \`product\`, \`kind\` (\`commented\` or \`approved\`), \`file_name\`, \`payload\` (an
   \`EvalLabelPayload\` as a JSON string: marks with their judged outcome, findings with their
   boxes) and \`pdf\` (the label PDF the marks were made on).
3. Stop there. Setting a run's status (draft, reviewed, archived), renaming and deleting are
   done by an admin in the console, not by an integration key.

Rules:
- Only publish runs scored by the judged method (match, then tie-break). Never publish raw
  matcher counts as recall.
- Run data is client label text and artwork. Keep downloads on internal storage only.
- If a checkout of pyxis-rc-ai-service is available, prefer \`scripts/eval-run.sh\`
  (\`publish\`, \`list\`, \`download\`), which makes these calls; see \`docs/eval-runs.md\`.

## Reading results

- \`GET /api/v1/eval-runs/{run_id}/export\` returns a whole run: figures, every label's marks
  and findings, and every remark left on it.

## Evaluation-run endpoints

${endpoints}
`
}
